import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import JobApplicationsView from 'components/JobView/JobApplicationsView/JobApplicationsView';
import JobInfoController from 'components/JobView/JobInfoController';
import MatchesView from 'components/MatchesView/MatchesView';
import SidebarTabs from 'components/SidebarTabs/SidebarTabs';

import {
  selectActiveJobDetails,
  selectJobs
} from 'features/jobs/jobsSelectors';
import { selectActiveUserId } from 'features/sidebar/sidebarSelectors';

import { useGetActiveSidebarItemId } from 'hooks/useGetActiveSidebarItemId';

import { SidebarTabsItem } from 'types/sidebar';

import { FALLBACK_NUMBER_VALUE } from 'constants/uiSettings';

import { ReactComponent as InfoIcon } from 'assets/icons/info-circle.svg';
import { ReactComponent as TickIcon } from 'assets/icons/tickSquare.svg';
import { ReactComponent as UserIcon } from 'assets/icons/user.svg';

import styles from './JobDetailsPanel.module.scss';

const JOB_SIDEBAR_ITEMS_IDS = {
  JOB: 1,
  MATCHES: 2,
  APPLICATIONS: 3
};

interface JobDetailsPanelProps {
  jobId?: string | null;
  openItemId?: number;
}

const JobDetailsPanel: FC<JobDetailsPanelProps> = ({
  jobId: jobIdProp,
  openItemId
}) => {
  // TODO: remove this after moving to split view
  const jobIdFromSidebar = useSelector(selectActiveUserId) as string | null;
  const jobId = jobIdProp ?? jobIdFromSidebar;
  const { t } = useTranslation();

  const jobs = useSelector(selectJobs);
  const activeJobDetails = useSelector(selectActiveJobDetails);
  const job = useMemo(() => {
    if (!jobId) {
      return null;
    }

    return (
      jobs.find((job) => job.id === jobId) ||
      (activeJobDetails?.id === jobId ? activeJobDetails : null)
    );
  }, [activeJobDetails, jobs, jobId]);

  const allowedTabs = new Set<number>(Object.values(JOB_SIDEBAR_ITEMS_IDS));
  const activeItemId = useGetActiveSidebarItemId(allowedTabs, openItemId);

  const items: SidebarTabsItem[] = [
    {
      id: JOB_SIDEBAR_ITEMS_IDS.JOB,
      title: t('sidebar.job.tab.info'),
      component: <JobInfoController jobId={jobId} />,
      icon: <InfoIcon />
    },
    {
      id: JOB_SIDEBAR_ITEMS_IDS.APPLICATIONS,
      title: t('sidebar.job.tab.applications', {
        value: job?.totalApplications || FALLBACK_NUMBER_VALUE
      }),
      component: (
        <JobApplicationsView jobId={jobId} isEmpty={!job?.totalApplications} />
      ),
      icon: <TickIcon />
    },
    {
      id: JOB_SIDEBAR_ITEMS_IDS.MATCHES,
      title: t('sidebar.job.tab.matches', {
        value: job?.totalMatches || FALLBACK_NUMBER_VALUE
      }),
      component: <MatchesView jobId={jobId} />,
      icon: <UserIcon />
    }
  ];

  if (!jobId) {
    return null;
  }

  return (
    <div className={styles.container}>
      {job?.title && (
        <h2 className={styles.panelTitle}>{job?.title}</h2>
      )}
      <SidebarTabs
        items={items}
        isChangeCurrentTabOnNewItems={true}
        openItemId={activeItemId}
      />
    </div>
  );
};

export default JobDetailsPanel;
